import { type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { trackEvent } from '@zep/logger/tracking';
import { useGetOwnedClassrooms } from '@/api/gen/classroom/classroom.gen';
import { useLocale } from '@/hooks';
import { ResetIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { AnimatedAvatar } from '@/shared/features/user-avatar';
import { selectData } from '@/shared/utils';
import { Button, IconButton } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { type AvatarEventSource } from '../events.quiz-profile-avatar';
import { type AvatarDecorateForm } from '../schema';
import { MyPointsChip } from './MyPointsChip';
import { RandomAvatarButton } from './RandomAvatarButton';
const TEACHER_GRANT_BANNER_IMAGES = {
  ko: '/assets/avatar-decorate-jelly-banner-ko@2x.webp',
  en: '/assets/avatar-decorate-jelly-banner-en@2x.webp',
  th: '/assets/avatar-decorate-jelly-banner-th@2x.webp',
  id: '/assets/avatar-decorate-jelly-banner-id@2x.webp',
  ja: '/assets/avatar-decorate-jelly-banner-ja@2x.webp'
} as const;
type TeacherGrantBannerLocale = keyof typeof TEACHER_GRANT_BANNER_IMAGES;

/** 링크로 쓰든 버튼으로 쓰든 겉모습은 같다. 두 갈래가 어긋나지 않게 묶어 둔다. */
const GRANT_BANNER_CLASSNAME = 'group relative mt-[19px] aspect-[268/88] w-full max-w-[268px] shrink-0 transition-[filter,transform] duration-150 hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 motion-reduce:transition-none';
const GrantBannerArt = ({
  imageUrl
}: {
  imageUrl: string;
}) => <>
    <Image src={imageUrl} width={268} height={107} alt=""
  // 이미 Figma 에서 2x webp(q90)로 뽑은 정적 에셋이라 next/image 재최적화
  // (q75 재인코딩 + 사이즈 버킷 다운스케일)는 화질만 떨어뜨린다 → 원본 그대로 서빙.
  unoptimized className="absolute left-0 top-[-19px] h-[107px] w-full" priority={false} data-sentry-element="Image" data-sentry-source-file="AvatarPreviewPanel.tsx" />
    <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[16px] [clip-path:inset(0_round_16px)]">
      <span className="absolute inset-0 animate-avatar-banner-shine bg-[linear-gradient(105deg,transparent_35%,rgba(255,255,255,0.18)_44%,rgba(255,255,255,0.58)_50%,rgba(255,255,255,0.18)_56%,transparent_65%)] bg-[length:200%_100%] bg-no-repeat motion-reduce:hidden" />
    </span>
  </>;

/**
 * 우측 프리뷰 패널 (Figma node 1342:31476 우측 섹션).
 * 상단: 랜덤 꾸미기 + 저장된 상태로 되돌리기 + 코인 잔액(학생만) / 중앙: 전신 아바타 /
 * 하단: 저장.
 */
export const AvatarPreviewPanel = ({
  onSave,
  isSaving,
  onResetToSaved,
  source,
  popover,
  canGrantJelly = true,
  onGrantBannerClick
}: {
  onSave: () => void;
  isSaving?: boolean;
  onResetToSaved: () => void;
  source: AvatarEventSource;
  /**
   * 우하단 젤리 안내 팝오버(시안 1811:87887 · 1823:95533). 저장 버튼 바로 위에 얹힌다.
   * 노출 여부·빈도는 호출부가 정한다.
   */
  popover?: ReactNode;
  /** 이 화면에서 실제로 젤리를 줄 수 있는지. 못 주면 지급 배너를 걷는다. */
  canGrantJelly?: boolean;
  /**
   * 젤리 배너를 눌렀을 때. 넘기면 **링크 대신 버튼**이 되어 화면을 떠나지 않는다.
   *
   * 플레이에서 연 아바타 꾸미기가 이 경우다 — 지금 같이 퀴즈를 푸는 학생들이 바로
   * 앞에 있는데 클래스 화면으로 보내면, 줄 대상을 두고 줄 자리를 떠나는 꼴이 된다.
   * 넘기지 않으면 기존대로 내 클래스로 이동한다(프로필 편집).
   */
  onGrantBannerClick?: () => void;
}) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale
  } = useLocale();
  const {
    watch,
    formState
  } = useFormContext<AvatarDecorateForm>();
  const {
    user
  } = useUser();
  const avatar = watch('avatar');
  // 선생님이라고 다 줄 수 있는 게 아니다. 남의 퀴즈에서는 서버가 지급을 막으므로,
  // 배너를 띄우면 눌렀을 때 「지급 권한이 없습니다」 로 끝난다 — 할 수 없는 일을
  // 권하는 배너다. 그래서 줄 수 있는 사람에게만 보여준다.
  const shouldShowTeacherGrantBanner = canGrantJelly && user?.type === 'teacher';
  const {
    data: ownedClassrooms = []
  } = useGetOwnedClassrooms(undefined, {
    query: {
      enabled: shouldShowTeacherGrantBanner,
      select: selectData,
      // 배너 링크 목적지(단일 클래스면 그 학생 목록, 아니면 전체 목록)만 정하는
      // 보조 조회다. 전역 기본값 throwOnError: true 를 그대로 두면 일시적인 500 이
      // 모달 안의 선택·저장까지 전역 오류 화면으로 날린다. 실패하면 기본 목록 링크로 둔다.
      throwOnError: false,
      meta: {
        ignoreError: true
      }
    }
  });
  const grantBannerHref = ownedClassrooms.length === 1 ? CLASSROOM_ROUTES.studentList(ownedClassrooms[0].classGroupId) : CLASSROOM_ROUTES.list();
  const language = locale.split('-')[0];
  const grantBannerImage = TEACHER_GRANT_BANNER_IMAGES[language as TeacherGrantBannerLocale] ?? TEACHER_GRANT_BANNER_IMAGES.en;
  return (
    // 팝오버가 absolute 로 뜨는 기준. relative 가 없으면 모달 전체를 기준으로 잡아
    // 엉뚱한 자리에 붙는다.
    <div className="relative flex h-full flex-col bg-neutral" data-sentry-component="AvatarPreviewPanel" data-sentry-source-file="AvatarPreviewPanel.tsx">
      <div className="flex shrink-0 items-center justify-between bg-neutral px-md pb-none pt-md">
        <div className="flex gap-xs">
          <IconButton variant="outlined" intent="neutral" size="md" rounded="default" disabled={!formState.isDirty} aria-label={t('avatarDecorate.restoreSavedAriaLabel')} onClick={onResetToSaved} data-sentry-element="IconButton" data-sentry-source-file="AvatarPreviewPanel.tsx">
            <ResetIcon size="sm" color={formState.isDirty ? ICON_COLOR.neutral : ICON_COLOR.disabled} data-sentry-element="ResetIcon" data-sentry-source-file="AvatarPreviewPanel.tsx" />
          </IconButton>
          <RandomAvatarButton iconOnly data-sentry-element="RandomAvatarButton" data-sentry-source-file="AvatarPreviewPanel.tsx" />
        </div>
        <MyPointsChip data-sentry-element="MyPointsChip" data-sentry-source-file="AvatarPreviewPanel.tsx" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center px-none py-none">
        <div className="flex min-h-0 flex-1 items-center justify-center self-stretch">
          <div className="flex h-[128px] w-[96px] shrink-0 items-center justify-center">
            {/*
              로그인 여부와 무관하게 **살아 있는 캐릭터**를 그린다.
              게스트에게만 baked 프로필 이미지를 쓴 적이 있는데, 그건 서버가 구워 둔
              그림이라 지금 고르는 중인 파츠를 따라오지 못한다 — 꾸미기 화면에서
              고를 때마다 안 바뀌는 미리보기는 미리보기가 아니다.
              (플레이 헤더의 baked 이미지는 별개다. 거기는 저장된 모습을 보여주는 자리다.)
             */}
            <AnimatedAvatar avatarData={avatar} behavior="stand" className="origin-center scale-[2]" data-sentry-element="AnimatedAvatar" data-sentry-source-file="AvatarPreviewPanel.tsx" />
          </div>
        </div>
        {shouldShowTeacherGrantBanner && (onGrantBannerClick ? <button type="button" aria-label={t('avatarDecorate.grantPointBanner')} onClick={() => {
          trackEvent('quiz_profile_point_grant_banner_click', {
            source,
            destination: 'jelly_grant'
          });
          onGrantBannerClick();
        }} className={GRANT_BANNER_CLASSNAME}>
              <GrantBannerArt imageUrl={grantBannerImage} />
            </button> : <Link href={grantBannerHref} prefetch={false} aria-label={t('avatarDecorate.grantPointBanner')} onClick={() => trackEvent('quiz_profile_point_grant_banner_click', {
          source,
          destination: ownedClassrooms.length === 1 ? 'student_list' : 'classroom_list',
          ...(ownedClassrooms.length === 1 && {
            classroom_id: ownedClassrooms[0].classGroupId
          })
        })} className={GRANT_BANNER_CLASSNAME}>
              <GrantBannerArt imageUrl={grantBannerImage} />
            </Link>)}
      </div>

      {/* 배너와 저장 버튼은 계속 보이게 두고, 팝오버만 그 위에 겹친다.
          흐름에 넣으면 아바타 프리뷰가 위로 밀려 화면이 출렁인다. */}
      {Boolean(popover) &&
      // 선생님은 지급 배너(107px) 위에, 학생은 배너가 없으므로 저장 버튼 바로 위에
      // 놓는다. 같은 bottom 값을 쓰면 학생 안내가 패널 한가운데로 떠 버린다.
      <div className={cn('pointer-events-none absolute inset-x-0 z-10 flex justify-center px-md', shouldShowTeacherGrantBanner ? 'bottom-[183px]' : 'bottom-[76px]')}>
          <div className="pointer-events-auto w-full max-w-[268px]">
            {popover}
          </div>
        </div>}

      <div className="flex shrink-0 flex-col gap-sm px-md pb-lg pt-sm">
        <Button variant="contained" intent="strong" size="md" className="w-full" disabled={!formState.isDirty || isSaving} onClick={onSave} data-sentry-element="Button" data-sentry-source-file="AvatarPreviewPanel.tsx">
          {t('avatarDecorate.save')}
        </Button>
      </div>
    </div>
  );
};